import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { ActiveOrganizationSignatureSummary } from '@pillar-portal/api-types';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { runInBackground } from '@/lib/utils';
import {
    ORGANIZATION_SIGNING_PATH,
    awaitingSignatureMessage,
    buildOrganizationSigningPath,
    claimOrganizationSigningPrompt,
    shouldPromptOrganizationSigning,
    type OrganizationSigningPromptStorage,
} from './organization-signing-prompt';
import { useActiveOrganizationSignatureSummary } from './useActiveOrganizationSignatureSummary';

const log = createComponentLogger('OrganizationAgreementBanner');
const promptedInPageLoad: Set<string> = new Set();

function getSessionStorage(): OrganizationSigningPromptStorage | null {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

/** The persistent notice, without the queries that feed it. */
export function OrganizationAgreementNotice({
    summary,
    returnTo,
}: {
    readonly summary: ActiveOrganizationSignatureSummary;
    readonly returnTo: string;
}) {
    if (summary.summary_kind !== 'organization' || summary.awaiting_signature_count === 0) {
        return null;
    }
    return (
        <div
            role="status"
            className="border-b border-amber-300 bg-amber-50 px-8 py-3 text-sm text-amber-900"
        >
            {awaitingSignatureMessage(summary)} Payment services are activated after signature.
            {summary.can_sign ? (
                <>
                    {' '}
                    <Link
                        className="font-medium underline"
                        to={buildOrganizationSigningPath(returnTo)}
                    >
                        Review and sign
                    </Link>
                </>
            ) : null}
        </div>
    );
}

/**
 * Surfaces Organization agreements awaiting signature. Signing does not gate
 * login, but services are activated only after signature. A PRINCIPAL is
 * taken to the signing screen once per session and can choose Sign later;
 * ADMIN and REP users only see that a principal still has to sign.
 */
export function OrganizationAgreementBanner() {
    const navigate = useNavigate();
    const location = useLocation();
    const { activeRoleAssignment, summary } = useActiveOrganizationSignatureSummary();
    const returnTo = `${location.pathname}${location.search}`;
    const roleAssignmentUuid = activeRoleAssignment?.role_assignment_uuid ?? null;
    useEffect(() => {
        if (
            roleAssignmentUuid === null ||
            !shouldPromptOrganizationSigning(summary, location.pathname) ||
            !claimOrganizationSigningPrompt(
                getSessionStorage(),
                roleAssignmentUuid,
                promptedInPageLoad
            )
        ) {
            return;
        }
        runInBackground(navigate(buildOrganizationSigningPath(returnTo)), (error: unknown) =>
            log.error('Unable to open the Organization signing screen', error)
        );
    }, [summary, roleAssignmentUuid, location.pathname, returnTo, navigate]);

    if (summary === undefined || location.pathname.startsWith(ORGANIZATION_SIGNING_PATH)) {
        return null;
    }
    return <OrganizationAgreementNotice summary={summary} returnTo={returnTo} />;
}
